import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Box, Flex, Heading, Text } from '@chakra-ui/react'
import { MapContainer, TileLayer, LayersControl, LayerGroup, CircleMarker, Circle, Popup } from 'react-leaflet'
import 'leaflet/dist/leaflet.css'
import { UiBreadcrumb } from 'components/common'
import { getMapData } from 'api'
import { useAppContext } from 'hooks/useAppContext'

const BreadcrumbNav = [
  {
    label: 'Map',
    isCurrentPage: true
  }
]

// Center of the continental US
const DEFAULT_CENTER = [39.8283, -98.5795]
const DEFAULT_ZOOM = 4
const MILES_TO_METERS = 1609.34

// Two deals at one site share coordinates. Nudge each pin a few meters so both stay clickable.
const spreadCoincidentPins = (projects) => {
  const groups = new Map()
  projects.forEach(project => {
    const key = `${Number(project.latitude).toFixed(5)}|${Number(project.longitude).toFixed(5)}`
    if (!groups.has(key)) groups.set(key, [])
    groups.get(key).push(project)
  })

  const placed = []
  groups.forEach(group => {
    if (group.length === 1) {
      placed.push(group[0])
      return
    }
    const radius = 0.00025
    group.forEach((project, index) => {
      const angle = (2 * Math.PI * index) / group.length
      const latitude = Number(project.latitude) + (radius * Math.cos(angle))
      const longitude = Number(project.longitude) + ((radius * Math.sin(angle)) / Math.cos(Number(project.latitude) * Math.PI / 180))
      placed.push({ ...project, latitude, longitude })
    })
  })
  return placed
}

const LAYER_STYLES = {
  projects: { color: '#E53E3E', label: 'Sold Signs (Projects)' },
  installation: { color: '#3182CE', label: 'Installation Vendors' },
  shipping: { color: '#38A169', label: 'Shipping Vendors' },
  emc: { color: '#805AD5', label: 'EMC Vendors' },
  fabrication: { color: '#DD6B20', label: 'Fabrication Vendors' }
}

const VendorLayer = ({ vendors = [], color, withRadius = false }) => {
  return (
    <LayerGroup>
      {vendors.map(v => (
        <LayerGroup key={v.id}>
          {withRadius && v.serviceRadiusMiles > 0 && (
            <Circle
              center={[v.latitude, v.longitude]}
              radius={v.serviceRadiusMiles * MILES_TO_METERS}
              pathOptions={{ color, fillColor: color, fillOpacity: 0.08, weight: 1 }}
            />
          )}
          <CircleMarker
            center={[v.latitude, v.longitude]}
            radius={7}
            pathOptions={{ color, fillColor: color, fillOpacity: 0.9 }}
          >
            <Popup>
              <Box fontSize="sm">
                <Text fontWeight="bold">{v.name}</Text>
                {v.contact && <Text>Contact: {v.contact}</Text>}
                {v.phone && <Text>{v.phone}</Text>}
                {v.email && <Text>{v.email}</Text>}
                {[v.address, v.city, v.state, v.zip].filter(Boolean).length > 0 && (
                  <Text>{[v.address, v.city, v.state, v.zip].filter(Boolean).join(', ')}</Text>
                )}
                {withRadius && v.serviceRadiusMiles > 0 && (
                  <Text>Service radius: {v.serviceRadiusMiles} mi</Text>
                )}
              </Box>
            </Popup>
          </CircleMarker>
        </LayerGroup>
      ))}
    </LayerGroup>
  )
}

VendorLayer.propTypes = {
  vendors: PropTypes.array,
  color: PropTypes.string,
  withRadius: PropTypes.bool
}

export const MapView = () => {
  const { createToast } = useAppContext()
  const [data, setData] = useState({ projects: [], vendors: { installation: [], shipping: [], emc: [], fabrication: [] } })

  useEffect(() => {
    const load = async () => {
      try {
        const response = await getMapData()
        setData({
          ...response,
          projects: spreadCoincidentPins(response.projects || [])
        })
      } catch (err) {
        createToast('error', 'Error', 'Failed to load map data')
      }
    }
    load()
  }, [])

  return (
    <>
      <UiBreadcrumb items={BreadcrumbNav} />
      <Box>
        <Flex justifyContent="space-between" alignItems="center" my={8}>
          <Heading size="lg">Map</Heading>
        </Flex>
        <Box height="75vh" width="100%" borderWidth={1} borderStyle="solid" borderColor="gray.200">
          <MapContainer center={DEFAULT_CENTER} zoom={DEFAULT_ZOOM} style={{ height: '100%', width: '100%' }} scrollWheelZoom>
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <LayersControl position="topright">
              <LayersControl.Overlay checked name={LAYER_STYLES.projects.label}>
                <LayerGroup>
                  {data.projects.map(p => (
                    <CircleMarker
                      key={p.id}
                      center={[p.latitude, p.longitude]}
                      radius={7}
                      pathOptions={{ color: LAYER_STYLES.projects.color, fillColor: LAYER_STYLES.projects.color, fillOpacity: 0.9 }}
                    >
                      <Popup>
                        <Box fontSize="sm">
                          <Text fontWeight="bold">{p.customerCompany}</Text>
                          {p.dealName && <Text>{p.dealName}</Text>}
                          {p.customerName && <Text>{p.customerName}</Text>}
                          {p.customerPhone && <Text>{p.customerPhone}</Text>}
                          {p.customerEmail && <Text>{p.customerEmail}</Text>}
                          {p.address && <Text>{p.address}</Text>}
                          {p.stage && <Text>Stage: {p.stage}</Text>}
                          {p.quoteNumber ? <Text>Quote: {[p.quoteType, p.quoteNumber].filter(Boolean).join(' #')}</Text> : null}
                          {p.balanceTerms && <Text>Balance Terms: {p.balanceTerms}</Text>}
                          {p.depositStatus && <Text>Deposit: {p.depositStatus}</Text>}
                          {p.permit && <Text>Permit: {p.permit}</Text>}
                          {p.installDate && <Text>Install: {new Date(p.installDate).toLocaleDateString()}</Text>}
                        </Box>
                      </Popup>
                    </CircleMarker>
                  ))}
                </LayerGroup>
              </LayersControl.Overlay>

              <LayersControl.Overlay checked name={LAYER_STYLES.installation.label}>
                <VendorLayer vendors={data.vendors.installation} color={LAYER_STYLES.installation.color} withRadius />
              </LayersControl.Overlay>

              <LayersControl.Overlay checked name={LAYER_STYLES.shipping.label}>
                <VendorLayer vendors={data.vendors.shipping} color={LAYER_STYLES.shipping.color} withRadius />
              </LayersControl.Overlay>

              <LayersControl.Overlay name={LAYER_STYLES.emc.label}>
                <VendorLayer vendors={data.vendors.emc} color={LAYER_STYLES.emc.color} />
              </LayersControl.Overlay>

              <LayersControl.Overlay name={LAYER_STYLES.fabrication.label}>
                <VendorLayer vendors={data.vendors.fabrication} color={LAYER_STYLES.fabrication.color} />
              </LayersControl.Overlay>
            </LayersControl>
          </MapContainer>
        </Box>
      </Box>
    </>
  )
}
